import type { ManifestValidationOperator } from "./validation-operators";

export interface DomainPropertyNoteSummary {
  notes: string[];
}

export interface MetamodelGraphqlMutation {
  label: string;
  query: string;
}

export type MetamodelNotesInput = string | string[] | undefined;

export interface DomainValidationRuleSummary {
  operator: string;
  stringValue?: string;
  numberValue?: number;
  booleanValue?: boolean;
  message?: string;
}

export interface DomainPropertySummary extends DomainPropertyNoteSummary {
  name: string;
  label?: string | null;
  path: string;
  description?: string | null;
  type?: string | null;
  searchable?: boolean | null;
  searchablePhonetic?: boolean | null;
  required?: { message?: string } | null;
  enumRule?: { values: string[]; labels?: string[]; message?: string } | null;
  filterBy?: false | { operators: string[]; scalarType?: string } | null;
  validationRules?: DomainValidationRuleSummary[];
}

export interface DomainRelationshipSummary {
  name?: string | null;
  localSubmodel?: string | null;
  localIsMany?: boolean;
  foreignModel?: string | null;
  foreignSubmodel?: string | null;
  foreignIsMany?: boolean;
  kind?: string | null;
}

export interface DomainMethodSummary {
  effectKey?: string;
  name: string;
  label?: string | null;
  description?: string | null;
  inputSchema?: any;
  outputSchema?: any;
  metamodels?: unknown | null;
  effectBehaviors?: DomainEffectBehaviorsSummary | null;
  creates?: DomainMethodCreationSummary | null;
  static?: boolean;
}

export interface DomainEffectPostConditionSummary {
  condition: string;
  description?: string;
}

export interface DomainEffectDryRunSummary {
  enabled: boolean;
  description?: string;
}

export interface DomainEffectReverseSummary {
  handler?: string;
  description?: string;
}

export interface DomainEffectApprovalRequiredSummary {
  required: boolean;
  reason?: string;
  mode?: string;
}

export interface DomainMethodCreationSummary {
  className: string;
  idPath?: string;
  pathPath?: string;
  statePath?: string;
  classStateHandle?: boolean;
}

export interface DomainEffectBehaviorsSummary {
  postCondition?: DomainEffectPostConditionSummary;
  dryRun?: DomainEffectDryRunSummary;
  reverse?: DomainEffectReverseSummary;
  approvalRequired?: DomainEffectApprovalRequiredSummary;
}

export interface DomainStateMachineStateSummary {
  name: string;
  label?: string | null;
  description?: string | null;
  isFinal: boolean;
}

export interface DomainStateMachineTransitionSummary {
  name: string;
  from: string;
  to: string;
  label?: string | null;
  description?: string | null;
  action?: ManifestStateTransitionActionInput | null;
  assignee?: ManifestStateTransitionAssigneeInput | null;
  requirements?: ManifestStateTransitionRequirementsInput | null;
  permission?: ManifestStateTransitionPermissionInput | string | null;
  risk?: "low" | "medium" | "high" | null;
  expectedOutcome?: ManifestStateTransitionExpectedOutcomeInput | string | null;
}

export interface DomainStateMachineSummary {
  name: string;
  stateField?: string | null;
  entryState?: string | null;
  finalStates: string[];
  states: DomainStateMachineStateSummary[];
  transitions: DomainStateMachineTransitionSummary[];
}

export interface DomainClassSummary {
  name: string;
  label?: string | null;
  description?: string | null;
  notes: string[];
  properties: DomainPropertySummary[];
  relationships: DomainRelationshipSummary[];
  methods: DomainMethodSummary[];
  stateMachines: DomainStateMachineSummary[];
}

export interface DomainTypeUnionIR {
  kind: "union";
  name: string;
  docs: string[];
  members: string[];
}

export type DomainTypeIR = DomainTypeUnionIR;

export interface DomainPropertyIR {
  name: string;
  type: string;
  readonly: boolean;
  docs: string[];
}

export interface DomainMethodParamIR {
  name: string;
  type: string;
  optional?: boolean;
}

export interface DomainEffectRuntimeIR {
  kind: "effect";
  effectKey: string;
}

export interface DomainRelationshipRuntimeIR {
  kind: "relationship";
  relationship: string;
  targetClassName: string;
  many: boolean;
}

export interface DomainStateMachineRuntimeIR {
  kind: "state_machine";
  machineName: string;
  className: string;
  stateTypeName: string;
  transitionTypeName: string;
  operation:
    | "get_current"
    | "reach"
    | "prepare_reach"
    | "prepare_create_reach"
    | "list_transitions"
    | "list_reachable_states"
    | "is_final"
    | "paths_to";
  targetState?: string | null;
  transitionActions?: Record<string, ManifestStateTransitionActionInput>;
  creation?: {
    methodName: string;
    effectKey: string;
    inputSchema?: any;
    outputSchema?: any;
    creates: DomainMethodCreationSummary;
  } | null;
}

export type DomainMethodRuntimeIR =
  | DomainEffectRuntimeIR
  | DomainRelationshipRuntimeIR
  | DomainStateMachineRuntimeIR;

export interface DomainMethodIR {
  name: string;
  docs: string[];
  static: boolean;
  params: DomainMethodParamIR[];
  returnType: string;
  runtime: DomainMethodRuntimeIR;
}

export interface DomainClassIR {
  name: string;
  docs: string[];
  properties: DomainPropertyIR[];
  methods: DomainMethodIR[];
  source: DomainClassSummary;
}

export interface DomainGlobalToolIR {
  summary: DomainMethodSummary;
  method: DomainMethodIR;
}

export interface DomainIR {
  classes: Record<string, DomainClassIR>;
  types: DomainTypeIR[];
  globalTools: DomainGlobalToolIR[];
}

export type { ManifestValidationOperator } from "./validation-operators";
export { VALIDATION_RULE_OPERATORS } from "./validation-operators";

export interface ManifestEnumRuleInput {
  values: string[];
  labels?: string[];
  message?: string;
}

export interface ManifestFilterByInput {
  operators: string[];
  scalarType?: string;
}

export interface ManifestSearchableInput {
  enabled?: boolean;
  phonetic?: boolean;
}

export interface ManifestValidationRuleInput {
  operator: ManifestValidationOperator;
  stringValue?: string;
  numberValue?: number;
  booleanValue?: boolean;
  message?: string;
}

export interface ManifestStateMachineStateInput {
  name: string;
  label?: string;
  description?: string;
  isFinal?: boolean;
}

export type ManifestStateTransitionInputBinding =
  | null
  | string
  | number
  | boolean
  | ManifestStateTransitionInputBinding[]
  | { [key: string]: ManifestStateTransitionInputBinding }
  | { const: unknown }
  | { from: "object"; path: string; editable?: boolean }
  | { from: "field"; name: string; editable?: boolean }
  | {
      from: "relationship";
      name: string;
      path?: string;
      many?: boolean;
      editable?: boolean;
    }
  | { from: "session"; path: string; editable?: boolean }
  | { from: "actor"; path: string; editable?: boolean };

export interface ManifestStateTransitionActionInput {
  effect: string;
  /** User-facing name of the connected system that performs the effect. */
  providerLabel?: string;
  input?: Record<string, ManifestStateTransitionInputBinding>;
}

export interface ManifestStateTransitionAssigneeInput {
  kind: string;
  from?: ManifestStateTransitionInputBinding;
  role?: string;
  label?: string;
}

export interface ManifestStateTransitionRelatedStateRequirementInput {
  relationship: string;
  machine: string;
  state: string;
  className?: string;
  label?: string;
  mode?: "every" | "some" | "any";
}

export interface ManifestStateTransitionRequirementsInput {
  fields?: string[];
  relationships?: string[];
  relatedStates?: ManifestStateTransitionRelatedStateRequirementInput[];
}

export interface ManifestStateTransitionPermissionInput {
  profile?: string;
  profileId?: string;
  label?: string;
  reason?: string;
}

export interface ManifestStateTransitionExpectedOutcomeInput {
  machine?: string;
  state: string;
  summary?: string;
}

export interface ManifestStateMachineTransitionInput {
  name: string;
  from: string;
  to: string;
  label?: string;
  description?: string;
  action?: ManifestStateTransitionActionInput;
  assignee?: ManifestStateTransitionAssigneeInput;
  requirements?: ManifestStateTransitionRequirementsInput;
  permission?: ManifestStateTransitionPermissionInput | string;
  risk?: "low" | "medium" | "high";
  expectedOutcome?: ManifestStateTransitionExpectedOutcomeInput | string;
}

export interface ManifestStateMachineInput {
  name: string;
  stateField?: string;
  entryState: string;
  states: Array<string | ManifestStateMachineStateInput>;
  transitions: ManifestStateMachineTransitionInput[];
  finalStates?: string[];
}

export interface ManifestPostConditionInput {
  condition: string;
  description?: string;
}

export interface ManifestDryRunInput {
  enabled?: boolean;
  description?: string;
}

export interface ManifestReverseInput {
  handler?: string;
  description?: string;
}

export interface ManifestApprovalRequiredInput {
  required?: boolean;
  reason?: string;
  mode?: string;
}

export type ManifestCreatesInput =
  | string
  | {
      className: string;
      idPath?: string;
      pathPath?: string;
      statePath?: string;
      classStateHandle?: boolean;
    };

export type ManifestPolicyOutcome = "allow" | "confirm" | "deny";
export type ManifestPolicyOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "exists";

export interface ManifestPolicyPredicateInput {
  path?: string | string[];
  operator: ManifestPolicyOperator;
  stringValue?: string;
  numberValue?: number;
  booleanValue?: boolean;
  value?: string | number | boolean | null;
}

export interface ManifestPolicyStateMachineInput {
  machine: string;
  operator: ManifestPolicyOperator;
  state?: string;
  stringValue?: string;
}

export type ManifestPolicyConditionInput =
  | { all: ManifestPolicyConditionInput[] }
  | { any: ManifestPolicyConditionInput[] }
  | { not: ManifestPolicyConditionInput }
  | { input: ManifestPolicyPredicateInput }
  | { object: ManifestPolicyPredicateInput }
  | { stateMachine: ManifestPolicyStateMachineInput };

export interface ManifestPolicyRuleInput {
  id?: string;
  reason?: string;
  when: ManifestPolicyConditionInput;
}

export interface ManifestPoliciesInput {
  allowWhen?: ManifestPolicyRuleInput[];
  confirmWhen?: ManifestPolicyRuleInput[];
  denyWhen?: ManifestPolicyRuleInput[];
}

export interface FieldMetamodelManifestSpec {
  type?: string;
  note?: MetamodelNotesInput;
  required?: boolean;
  enum?: string[] | ManifestEnumRuleInput;
  searchable?: boolean | ManifestSearchableInput;
  filterBy?: boolean | string[] | ManifestFilterByInput;
  validate?: ManifestValidationRuleInput[];
}

export interface ModelMetamodelManifestSpec {
  note?: MetamodelNotesInput;
  stateMachines?: ManifestStateMachineInput[];
}

export interface EffectMetamodelManifestSpec {
  postCondition?: string | ManifestPostConditionInput;
  dryRun?: boolean | ManifestDryRunInput;
  reverse?: string | ManifestReverseInput;
  approvalRequired?: boolean | ManifestApprovalRequiredInput;
  creates?: ManifestCreatesInput;
  policies?: ManifestPoliciesInput;
}

export interface MetamodelSummarySelections {
  classFields?: string[];
  propertyFields?: string[];
  methodFields?: string[];
}

export interface MetamodelSummaryReader {
  selections?: MetamodelSummarySelections;
  readClassSummary?: (
    rawClass: Record<string, any>,
  ) => Partial<DomainClassSummary>;
  readPropertySummary?: (
    rawProperty: Record<string, any>,
  ) => Partial<DomainPropertySummary>;
  readMethodSummary?: (
    rawMethod: Record<string, any>,
  ) => Partial<DomainMethodSummary>;
}

export interface MetamodelDomainIRFacet {
  applyToClassIR?: (
    classIR: DomainClassIR,
    classSummary: DomainClassSummary,
  ) => DomainClassIR;
  applyToPropertyIR?: (
    propertyIR: DomainPropertyIR,
    propertySummary: DomainPropertySummary,
    classSummary: DomainClassSummary,
  ) => DomainPropertyIR;
  applyToMethodIR?: (
    methodIR: DomainMethodIR,
    methodSummary: DomainMethodSummary,
    classSummary?: DomainClassSummary,
  ) => DomainMethodIR;
  extraTypesForClass?: (classSummary: DomainClassSummary) => DomainTypeIR[];
  extraMethodsForClass?: (classSummary: DomainClassSummary) => DomainMethodIR[];
}

export interface MetamodelManifestFacet {
  buildFieldMutations?: (
    fieldPath: string,
    spec: FieldMetamodelManifestSpec,
  ) => MetamodelGraphqlMutation[];
  buildModelMutations?: (
    modelPath: string,
    spec: ModelMetamodelManifestSpec,
  ) => MetamodelGraphqlMutation[];
  buildEffectMutations?: (
    toolPath: string,
    spec: EffectMetamodelManifestSpec,
  ) => MetamodelGraphqlMutation[];
}

export interface MetamodelGraphqlResolverTools {
  run<T>(effect: unknown): Promise<T>;
  stateMachines?: {
    pathsToState(model: any, machineName: string, state: string): Promise<any>;
    instancesInState(
      model: any,
      machineName: string,
      state: string,
    ): Promise<any>;
    instancePopulation(
      model: any,
      machineName: string,
      state: string,
      page?: number,
      perPage?: number,
    ): Promise<any>;
  };
}

export type MetamodelGraphqlResolverMap = Record<
  string,
  Record<string, (parent: any, args: any) => any>
>;

export interface MetamodelGraphqlFacet {
  typeDefs?: string[];
  createResolvers?: (
    tools: MetamodelGraphqlResolverTools,
  ) => MetamodelGraphqlResolverMap;
}

export interface ManifestMetamodelDocRow {
  key: string;
  description: string;
}

export interface MetamodelDocsFacet {
  fieldRows?: ManifestMetamodelDocRow[];
  modelRows?: ManifestMetamodelDocRow[];
  effectRows?: ManifestMetamodelDocRow[];
}

export interface MetamodelPackage {
  id: string;
  summary?: MetamodelSummaryReader;
  domain?: MetamodelDomainIRFacet;
  manifest?: MetamodelManifestFacet;
  graphql?: MetamodelGraphqlFacet;
  docs?: MetamodelDocsFacet;
}

export interface ResolvedMetamodelPackage {
  id: string;
  summary: MetamodelSummaryReader;
  domain: MetamodelDomainIRFacet;
  manifest: MetamodelManifestFacet;
  graphql: MetamodelGraphqlFacet;
  docs: MetamodelDocsFacet;
}

export interface MetamodelRegistry {
  readonly packages: ResolvedMetamodelPackage[];
  collectDocRows(
    kind: "fieldRows" | "modelRows" | "effectRows",
  ): ManifestMetamodelDocRow[];
  getSummarySelections(): {
    classFields: string[];
    propertyFields: string[];
    methodFields: string[];
  };
  applyClassSummaryReaders(
    rawClass: Record<string, any>,
    classSummary: DomainClassSummary,
  ): DomainClassSummary;
  applyPropertySummaryReaders(
    rawProperty: Record<string, any>,
    propertySummary: DomainPropertySummary,
  ): DomainPropertySummary;
  applyMethodSummaryReaders(
    rawMethod: Record<string, any>,
    methodSummary: DomainMethodSummary,
  ): DomainMethodSummary;
  applyClassIR(
    classIR: DomainClassIR,
    classSummary: DomainClassSummary,
  ): DomainClassIR;
  applyPropertyIR(
    propertyIR: DomainPropertyIR,
    propertySummary: DomainPropertySummary,
    classSummary: DomainClassSummary,
  ): DomainPropertyIR;
  applyMethodIR(
    methodIR: DomainMethodIR,
    methodSummary: DomainMethodSummary,
    classSummary?: DomainClassSummary,
  ): DomainMethodIR;
  getExtraTypesForClass(classSummary: DomainClassSummary): DomainTypeIR[];
  getExtraMethodsForClass(classSummary: DomainClassSummary): DomainMethodIR[];
}

export function defineMetamodelPackage(
  metamodelPackage: MetamodelPackage,
): ResolvedMetamodelPackage {
  return {
    id: metamodelPackage.id,
    summary: metamodelPackage.summary || {},
    domain: metamodelPackage.domain || {},
    manifest: metamodelPackage.manifest || {},
    graphql: metamodelPackage.graphql || {},
    docs: metamodelPackage.docs || {},
  };
}

function unique(values: string[]) {
  return [...new Set(values.filter(Boolean))];
}

function ensureUniqueMetamodelIds(packages: ResolvedMetamodelPackage[]) {
  const seen = new Set<string>();

  for (const metamodelPackage of packages) {
    if (seen.has(metamodelPackage.id)) {
      throw new Error(`Duplicate metamodel package id: ${metamodelPackage.id}`);
    }
    seen.add(metamodelPackage.id);
  }
}

export function resolveMetamodelPackages(
  metamodelPackages: MetamodelPackage[],
): ResolvedMetamodelPackage[] {
  const resolved = metamodelPackages.map(defineMetamodelPackage);
  ensureUniqueMetamodelIds(resolved);
  return resolved;
}

export function collectMetamodelDocRows(
  packages: ResolvedMetamodelPackage[],
  kind: "fieldRows" | "modelRows" | "effectRows",
): ManifestMetamodelDocRow[] {
  return packages.flatMap((pkg) => pkg.docs[kind] || []);
}

export function collectMetamodelSummarySelections(
  packages: ResolvedMetamodelPackage[],
) {
  const classFields: string[] = [];
  const propertyFields: string[] = [];
  const methodFields: string[] = [];

  for (const metamodelPackage of packages) {
    pushUnique(
      classFields,
      metamodelPackage.summary.selections?.classFields || [],
    );
    pushUnique(
      propertyFields,
      metamodelPackage.summary.selections?.propertyFields || [],
    );
    pushUnique(
      methodFields,
      metamodelPackage.summary.selections?.methodFields || [],
    );
  }

  return {
    classFields: unique(classFields),
    propertyFields: unique(propertyFields),
    methodFields: unique(methodFields),
  };
}

export function applyMetamodelSummaryReadersToClass(
  packages: ResolvedMetamodelPackage[],
  rawClass: Record<string, any>,
  classSummary: DomainClassSummary,
) {
  for (const metamodelPackage of packages) {
    const patch = metamodelPackage.summary.readClassSummary?.(rawClass);
    if (patch) {
      mergeClassSummaryPatch(classSummary, patch);
    }
  }

  return classSummary;
}

export function applyMetamodelSummaryReadersToProperty(
  packages: ResolvedMetamodelPackage[],
  rawProperty: Record<string, any>,
  propertySummary: DomainPropertySummary,
) {
  for (const metamodelPackage of packages) {
    const patch = metamodelPackage.summary.readPropertySummary?.(rawProperty);
    if (patch) {
      mergePropertySummaryPatch(propertySummary, patch);
    }
  }

  return propertySummary;
}

export function applyMetamodelSummaryReadersToMethod(
  packages: ResolvedMetamodelPackage[],
  rawMethod: Record<string, any>,
  methodSummary: DomainMethodSummary,
) {
  for (const metamodelPackage of packages) {
    const patch = metamodelPackage.summary.readMethodSummary?.(rawMethod);
    if (patch) {
      mergeMethodSummaryPatch(methodSummary, patch);
    }
  }

  return methodSummary;
}

export function applyMetamodelPropertyIR(
  packages: ResolvedMetamodelPackage[],
  propertyIR: DomainPropertyIR,
  propertySummary: DomainPropertySummary,
  classSummary: DomainClassSummary,
) {
  let current = propertyIR;

  for (const metamodelPackage of packages) {
    if (!metamodelPackage.domain.applyToPropertyIR) continue;
    current = metamodelPackage.domain.applyToPropertyIR(
      current,
      propertySummary,
      classSummary,
    );
  }

  return current;
}

export function applyMetamodelClassIR(
  packages: ResolvedMetamodelPackage[],
  classIR: DomainClassIR,
  classSummary: DomainClassSummary,
) {
  let current = classIR;

  for (const metamodelPackage of packages) {
    if (!metamodelPackage.domain.applyToClassIR) continue;
    current = metamodelPackage.domain.applyToClassIR(current, classSummary);
  }

  return current;
}

export function applyMetamodelMethodIR(
  packages: ResolvedMetamodelPackage[],
  methodIR: DomainMethodIR,
  methodSummary: DomainMethodSummary,
  classSummary?: DomainClassSummary,
) {
  let current = methodIR;

  for (const metamodelPackage of packages) {
    if (!metamodelPackage.domain.applyToMethodIR) continue;
    current = metamodelPackage.domain.applyToMethodIR(
      current,
      methodSummary,
      classSummary,
    );
  }

  return current;
}

export function getMetamodelExtraTypesForClass(
  packages: ResolvedMetamodelPackage[],
  classSummary: DomainClassSummary,
) {
  return packages.flatMap(
    (metamodelPackage) =>
      metamodelPackage.domain.extraTypesForClass?.(classSummary) || [],
  );
}

export function getMetamodelExtraMethodsForClass(
  packages: ResolvedMetamodelPackage[],
  classSummary: DomainClassSummary,
) {
  return packages.flatMap(
    (metamodelPackage) =>
      metamodelPackage.domain.extraMethodsForClass?.(classSummary) || [],
  );
}

function serializeGraphqlResolverFactorySource(source: string) {
  const trimmed = source.trim();
  if (
    trimmed.startsWith("function") ||
    trimmed.startsWith("(") ||
    trimmed.startsWith("async ") ||
    trimmed.startsWith("({")
  ) {
    return trimmed;
  }

  if (/^[A-Za-z_$][\w$]*\s*\(/.test(trimmed)) {
    return `function ${trimmed}`;
  }

  return trimmed;
}

export function renderMetamodelGraphqlSnapshotSource(
  packages: ResolvedMetamodelPackage[],
) {
  const typeDefs = packages.flatMap((pkg) => pkg.graphql.typeDefs || []);

  const factories = packages.flatMap((pkg) => {
    const factory = pkg.graphql.createResolvers;
    if (!factory) return [];
    return [
      `// ${pkg.id}\n${serializeGraphqlResolverFactorySource(factory.toString())}`,
    ];
  });

  return `// @ts-nocheck
// Auto-generated from per-metamodel GraphQL facets — DO NOT EDIT.
// Run \`bun run generate:metamodel-graphql\` from the repo root to refresh.

import { Effect } from "effect";
import * as metamodels from "./methods/metamodels";

type ResolverMap = Record<string, Record<string, (parent: any, args: any) => any>>;

const METAMODEL_GRAPHQL_TYPEDEFS = ${JSON.stringify(typeDefs, null, 2)};

const METAMODEL_GRAPHQL_RESOLVER_FACTORIES = [
${factories.map((factory) => `  (${factory}),`).join("\n")}
];

function mergeResolverMaps(resolverMaps: ResolverMap[]): ResolverMap {
  const merged: ResolverMap = {};

  for (const resolverMap of resolverMaps) {
    for (const [typeName, fieldResolvers] of Object.entries(resolverMap || {})) {
      if (!merged[typeName]) {
        merged[typeName] = {};
      }
      Object.assign(merged[typeName], fieldResolvers);
    }
  }

  return merged;
}

export function getMetamodelGraphqlTypeDefs() {
  return METAMODEL_GRAPHQL_TYPEDEFS.join("\\n\\n");
}

export function getMetamodelGraphqlResolvers(): ResolverMap {
  const run = (effect: unknown) => Effect.runPromise(effect as any);

  return mergeResolverMaps(
    METAMODEL_GRAPHQL_RESOLVER_FACTORIES.map((createResolvers) =>
      createResolvers({
        run,
        stateMachines: {
          pathsToState: async (model: any, machineName: string, state: string) =>
            await metamodels.paths_to_state(model, machineName, state),
          instancesInState: async (model: any, machineName: string, state: string) =>
            await metamodels.instances_in_state(model, machineName, state),
          instancePopulation: async (model: any, machineName: string, state: string, page?: number, perPage?: number) =>
            await metamodels.instance_population(model, machineName, state, page, perPage),
        },
      }),
    ),
  );
}
`;
}

export function createMetamodelRegistry(
  metamodelPackages: MetamodelPackage[],
): MetamodelRegistry {
  const packages = resolveMetamodelPackages(metamodelPackages);

  return {
    packages,
    collectDocRows(kind) {
      return collectMetamodelDocRows(packages, kind);
    },
    getSummarySelections() {
      return collectMetamodelSummarySelections(packages);
    },
    applyClassSummaryReaders(rawClass, classSummary) {
      return applyMetamodelSummaryReadersToClass(
        packages,
        rawClass,
        classSummary,
      );
    },
    applyPropertySummaryReaders(rawProperty, propertySummary) {
      return applyMetamodelSummaryReadersToProperty(
        packages,
        rawProperty,
        propertySummary,
      );
    },
    applyMethodSummaryReaders(rawMethod, methodSummary) {
      return applyMetamodelSummaryReadersToMethod(
        packages,
        rawMethod,
        methodSummary,
      );
    },
    applyClassIR(classIR, classSummary) {
      return applyMetamodelClassIR(packages, classIR, classSummary);
    },
    applyPropertyIR(propertyIR, propertySummary, classSummary) {
      return applyMetamodelPropertyIR(
        packages,
        propertyIR,
        propertySummary,
        classSummary,
      );
    },
    applyMethodIR(methodIR, methodSummary, classSummary) {
      return applyMetamodelMethodIR(
        packages,
        methodIR,
        methodSummary,
        classSummary,
      );
    },
    getExtraTypesForClass(classSummary) {
      return getMetamodelExtraTypesForClass(packages, classSummary);
    },
    getExtraMethodsForClass(classSummary) {
      return getMetamodelExtraMethodsForClass(packages, classSummary);
    },
  };
}

export function pushUnique(
  target: string[],
  values: Array<string | null | undefined>,
) {
  for (const value of values) {
    if (!value) continue;
    if (!target.includes(value)) target.push(value);
  }
}

export function mergeClassSummaryPatch(
  target: DomainClassSummary,
  patch: Partial<DomainClassSummary>,
) {
  pushUnique(target.notes, patch.notes || []);

  if (patch.stateMachines?.length) {
    for (const machine of patch.stateMachines) {
      const already = target.stateMachines.some(
        (existing) => existing.name === machine.name,
      );
      if (!already) target.stateMachines.push(machine);
    }
  }
}

export function mergePropertySummaryPatch(
  target: DomainPropertySummary,
  patch: Partial<DomainPropertySummary>,
) {
  pushUnique(target.notes, patch.notes || []);
  if (patch.searchable !== undefined) target.searchable = patch.searchable;
  if (patch.searchablePhonetic !== undefined) {
    target.searchablePhonetic = patch.searchablePhonetic;
  }
  if (patch.required !== undefined) target.required = patch.required;
  if (patch.enumRule !== undefined) target.enumRule = patch.enumRule;
  if (patch.filterBy !== undefined) target.filterBy = patch.filterBy;
  if (patch.validationRules !== undefined) {
    target.validationRules = patch.validationRules;
  }
}

export function mergeMethodSummaryPatch(
  target: DomainMethodSummary,
  patch: Partial<DomainMethodSummary>,
) {
  if (patch.effectKey !== undefined) target.effectKey = patch.effectKey;
  if (patch.description !== undefined) target.description = patch.description;
  if (patch.inputSchema !== undefined) target.inputSchema = patch.inputSchema;
  if (patch.outputSchema !== undefined)
    target.outputSchema = patch.outputSchema;
  if (patch.metamodels !== undefined) target.metamodels = patch.metamodels;
  if (patch.effectBehaviors !== undefined)
    target.effectBehaviors = patch.effectBehaviors;
  if (patch.creates !== undefined) target.creates = patch.creates;
  if (patch.static !== undefined) target.static = patch.static;
}

export function quoteTsStringLiteral(value: string) {
  return JSON.stringify(value);
}

export function toPascalCase(value: string): string {
  return value
    .split(/[_:\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

export function normalizeNotesInput(input?: MetamodelNotesInput): string[] {
  if (!input) return [];
  return (Array.isArray(input) ? input : [input]).filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );
}

export {
  EffectMetamodelSchema,
  EnumRuleSchema,
  FilterBySchema,
  NoteSchema,
  SearchableSchema,
  StateMachineSchema,
  StateMachineStateSchema,
  StateMachineTransitionSchema,
  ValidationRuleSchema,
} from "./manifest-zod";
